import React, { useEffect, useState } from 'react';
import { Scheduler } from '@aldabil/react-scheduler';
import { Checkbox, Segment, Header as SemanticHeader, Message, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { fetchWithAuth, getCSRFToken, useAuth, getUsername, isAssociateManagerOrAbove, isTeamManager } from '../auth';
import { LoadingWrapper, ScheduleEventEditor } from '../common';
import './TeamCalendar.css';

const TEAM_COLORS = [
    '#9b59b6', '#27ae60', '#f39c12', '#e74c3c', '#3498db',
    '#1abc9c', '#e91e63', '#9c27b0', '#ff5722', '#00bcd4',
    '#8bc34a', '#ff9800', '#673ab7', '#009688', '#cddc39',
];

const SCHEDULER_CONFIG = {
    height: 700,
    editable: true,
    deletable: false,
    draggable: false,
    weekDays: [0, 1, 2, 3, 4, 5, 6],
    weekStartOn: 0,
    startHour: 6,
    endHour: 22,
    step: 60,
};

const formatMemberEvent = (event, teamIds, colorMap, currentUsername) => {
    const eventTeamId = event.teams?.find(t => teamIds.includes(t.id))?.id || teamIds[0];
    
    // Check if this is an OOO (Out of Office) event
    const eventType = event.type || '';
    const eventTitle = event.title || '';
    const isOOO = eventType.toLowerCase() === 'pto' || 
                  eventType.toLowerCase() === 'sick' || 
                  eventType.toLowerCase().includes('out of office') ||
                  eventTitle.toLowerCase().includes('ooo') ||
                  eventTitle.toLowerCase().includes('out of office');
    
    // Format title with OOO indicator if applicable
    const titlePrefix = isOOO ? '[OOO] ' : '';
    
    const memberId = event.member.userId || event.member.user_id;
    const isOwn = memberId === currentUsername;

    const datetimeAllowedToDelete = new Date();
    if (isAssociateManagerOrAbove()) {
        datetimeAllowedToDelete.setHours(0, 0, 0, 0);
    } else {
        datetimeAllowedToDelete.setHours(23, 59, 59, 999);
    }
    const isDeletable = isOwn && new Date(event.end) > datetimeAllowedToDelete;

    return {
        event_id: `member-${event.id}`,
        title: `${titlePrefix}${event.member.name} - ${event.title}`,
        start: new Date(event.start),
        end: new Date(event.end),
        color: colorMap[eventTeamId],
        editable: isOwn,
        deletable: isDeletable,
        teamId: eventTeamId,
        memberName: event.member.name,
        memberId,
        eventTitle: event.title,
        notes: event.notes || '',
        type: event.type || '',
        approvalStatus: event.approvalStatus || '',
        designatedApprover: event.designatedApprover || null,
        eventType: 'member',
        isOOO: isOOO,
        memberRole: event.member.role || '',
    };
};

const formatTeamEvent = (event, teamIds, colorMap) => {
    const eventTeamId = typeof event.team === 'number' ? event.team : (event.team?.id || teamIds[0]);
    return {
        event_id: `team-${event.id}`,
        title: event.title,
        start: new Date(event.start),
        end: new Date(event.end),
        color: colorMap[eventTeamId],
        editable: false,
        deletable: false,
        teamId: eventTeamId,
        notes: event.notes || '',
        type: event.type || '',
        eventType: 'team',
    };
};

export const TeamCalendarView = () => {
    const auth = useAuth();
    const [teams, setTeams] = useState([]);
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [teamColors, setTeamColors] = useState({});
    const [isICCSupport, setIsICCSupport] = useState(false);
    const [teamVacationLimits, setTeamVacationLimits] = useState({});
    const [vacationLimitOverrides, setVacationLimitOverrides] = useState([]);

    const fetchUserTeams = async () => {
        if (!auth.username) return { teamIds: [], colors: {} };
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}teams/`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            );
            if (!response.ok) throw new Error('Failed to fetch teams');
            const allTeams = await response.json();

            const isSupportRole = auth.userIsIccSupportTeam ?? false;
            setIsICCSupport(isSupportRole);

            const associatedIds = (auth.associatedTeams || []).map(t => t.id);
            const teamsData = isSupportRole ? allTeams : allTeams.filter(t => associatedIds.includes(t.id));

            const teamIds = teamsData.map(t => t.id);
            const colors = Object.fromEntries(
                teamsData.map((team, i) => [team.id, TEAM_COLORS[i % TEAM_COLORS.length]])
            );
            const limits = Object.fromEntries(
                teamsData
                    .filter(t => t.vacationLimit != null || t.teamType?.vacationLimit != null)
                    .map(t => [t.id, t.vacationLimit ?? t.teamType?.vacationLimit])
            );

            setTeams(teamsData);
            setSelectedTeamIds(teamIds);
            setTeamColors(colors);
            setTeamVacationLimits(limits);

            return { teamIds, colors };
        } catch (err) {
            console.error('Error fetching teams:', err);
            setError('Failed to load teams');
            return { teamIds: [], colors: {} };
        }
    };

    const fetchTeamEvents = async (teamIds, colors = null) => {
        if (teamIds.length === 0) {
            setEvents([]);
            return;
        }

        const colorMap = colors || teamColors;

        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}events/calendar/?team_ids=${teamIds.join(',')}`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            );

            if (!response.ok) throw new Error('Failed to fetch calendar events');

            const { memberEvents = [], teamEvents = [] } = await response.json();

            const currentUsername = getUsername();
            const allEvents = [
                ...memberEvents.map(event => formatMemberEvent(event, teamIds, colorMap, currentUsername)),
                ...teamEvents.map(event => formatTeamEvent(event, teamIds, colorMap)),
            ];

            setEvents(allEvents);
        } catch (err) {
            console.error('Error fetching events:', err);
            setError('Failed to load events');
        }

        // Always refresh overrides alongside events so the pre-check never uses stale data.
        // The endpoint is scoped to the user's own teams so ICC Support gets all, others get theirs.
        try {
            const overrideResp = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}vacation-limit-overrides/`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            );
            if (overrideResp.ok) setVacationLimitOverrides(await overrideResp.json());
        } catch (err) {
            console.error('Error refreshing vacation overrides:', err);
        }
    };

    const fetchWithLoading = async (fetchFn) => {
        setLoading(true);
        await fetchFn();
        setLoading(false);
    };

    useEffect(() => {
        if (!auth.username) return;

        const initializeData = async () => {
            const { teamIds, colors } = await fetchUserTeams();
            await fetchTeamEvents(teamIds, colors);
        };

        fetchWithLoading(initializeData);
    }, [auth.username, auth.associatedTeams]);

    const handleDelete = async (eventId) => {
        const actualId = String(eventId).replace('member-', '');
        setError('');
        const resp = await fetchWithAuth(
            `${process.env.REACT_APP_ICC_TOOL_API_HOST}members/${getUsername()}/schedule/${actualId}/`,
            {
                headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                credentials: 'include',
                method: 'DELETE',
            }
        );
        if (!resp.ok) setError('Something went wrong while deleting the event.');
        await fetchWithLoading(() => fetchTeamEvents(selectedTeamIds));
    };

    const escalateCalendarEvent = async (eventId) => {
        const actualId = String(eventId).replace('member-', '');
        setError('');
        const resp = await fetchWithAuth(
            `${process.env.REACT_APP_ICC_TOOL_API_HOST}events/${actualId}/escalate/`,
            { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include', method: 'POST' }
        );
        if (!resp.ok) setError('Something went wrong.');
        await fetchWithLoading(() => fetchTeamEvents(selectedTeamIds));
    };

    const handleTeamToggle = async (teamId) => {
        const newSelectedTeams = selectedTeamIds.includes(teamId)
            ? selectedTeamIds.filter(id => id !== teamId)
            : [...selectedTeamIds, teamId];

        setSelectedTeamIds(newSelectedTeams);
        await fetchWithLoading(() => fetchTeamEvents(newSelectedTeams));
    };

    const handleSelectAll = async () => {
        const newTeamIds = selectedTeamIds.length === teams.length ? [] : teams.map(team => team.id);
        setSelectedTeamIds(newTeamIds);
        await fetchWithLoading(() => fetchTeamEvents(newTeamIds));
    };


    function getVacationLimitError(startDate, endDate) {
        if (isICCSupport || isTeamManager() || !startDate) return null;

        const schedulingMemberId = getUsername();
        const rangeStart = new Date(startDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(endDate || startDate);
        rangeEnd.setHours(0, 0, 0, 0);

        const userTeamIds = (auth.associatedTeams || []).map(t => t.id);

        const formatLocalDateStr = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const getOverrideForDay = (teamId, dayStr) => vacationLimitOverrides.find(
            o => o.team.id === teamId && o.startDate <= dayStr && o.endDate >= dayStr
        );

        for (let day = new Date(rangeStart); day <= rangeEnd; day.setDate(day.getDate() + 1)) {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);
            const dayStr = formatLocalDateStr(dayStart);

            for (const teamId of userTeamIds) {
                const override = getOverrideForDay(teamId, dayStr);
                const limit = override !== undefined ? override.limit : teamVacationLimits[teamId];
                if (!limit) continue;

                // Count other production-role members' OOO only — matches API vacation limit
                // (excludes self and non-production roles like Manager/Associate Manager).
                const overlappingCount = events.filter(e =>
                    e.memberId !== schedulingMemberId &&
                    e.teamId === teamId &&
                    e.eventType === 'member' &&
                    e.isOOO &&
                    !['Manager', 'Associate Manager'].includes(e.memberRole) &&
                    new Date(e.start) < dayEnd &&
                    new Date(e.end) > dayStart
                ).length;

                if (overlappingCount >= limit) {
                    return 'No Time Off available. Please work with your supervisor if needed.';
                }
            }
        }
        return null;
    }

    return (
        <div className="team-calendar-container">
            <SemanticHeader as="h2">Team Calendar</SemanticHeader>

            {error && (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <LoadingWrapper loading={loading} text="Loading calendar...">
                {(isICCSupport || teams.length > 1) && teams.length > 0 && (
                    <Segment className="team-filter-segment">
                        <div className="filter-header">
                            <SemanticHeader as="h4">Filter by Team</SemanticHeader>
                            {isICCSupport && (
                                <Checkbox
                                    label="Select All / Deselect All"
                                    checked={selectedTeamIds.length === teams.length}
                                    onChange={handleSelectAll}
                                    style={{ marginLeft: 'auto' }}
                                />
                            )}
                        </div>

                        <div className="team-checkboxes">
                            <div className="team-checkboxes-grid">
                                {teams.map(team => (
                                    <div key={team.id} className="team-checkbox-item">
                                        <Checkbox
                                            label={
                                                <label>
                                                    <span
                                                        className="color-indicator"
                                                        style={{ backgroundColor: teamColors[team.id] }}
                                                    />
                                                    {team.name}
                                                </label>
                                            }
                                            checked={selectedTeamIds.includes(team.id)}
                                            onChange={() => handleTeamToggle(team.id)}
                                            disabled={!isICCSupport && teams.length === 1}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Segment>
                )}

                {teams.length === 0 && !loading && (
                    <Message info>
                        <Message.Header>No Teams Found</Message.Header>
                        <p>You don't have any teams {isICCSupport ? 'available' : 'assigned to you'}.</p>
                    </Message>
                )}

                {teams.length > 0 && (
                    <>
                        <Message info style={{ marginBottom: '1rem' }}>
                            <Message.Header>Legend</Message.Header>
                            <p>
                                🏖️ [OOO] - Out of Office events (PTO, Sick, etc.) are clearly marked with this indicator and displayed in gray.
                            </p>
                        </Message>
                        
                        <div className="scheduler-wrapper">
                            <Scheduler
                                view="month"
                                events={events}
                                height={SCHEDULER_CONFIG.height}
                                editable={!auth.isReadOnly && SCHEDULER_CONFIG.editable}
                                deletable={(event) => !auth.isReadOnly && !!event.deletable}
                                draggable={SCHEDULER_CONFIG.draggable}
                                customEditor={(scheduler) => {
                                    const currentUser = getUsername();
                                    const userExistingEvents = events.filter(
                                        e => e.memberId === currentUser && e.eventType === 'member'
                                    );
                                    return (
                                        <ScheduleEventEditor
                                            scheduler={scheduler}
                                            memberId={currentUser}
                                            eventId={scheduler.edited ? String(scheduler.edited.event_id).replace('member-', '') : null}
                                            titleField="eventTitle"
                                            onSuccess={() => fetchWithLoading(() => fetchTeamEvents(selectedTeamIds))}
                                            setError={setError}
                                            checkVacationLimit={scheduler.edited ? null : (start, end) => getVacationLimitError(start, end)}
                                            existingEvents={userExistingEvents}
                                        />
                                    );
                                }}
                                onDelete={handleDelete}
                                week={{ weekDays: SCHEDULER_CONFIG.weekDays, weekStartOn: SCHEDULER_CONFIG.weekStartOn, startHour: SCHEDULER_CONFIG.startHour, endHour: SCHEDULER_CONFIG.endHour, step: SCHEDULER_CONFIG.step }}
                                month={{ weekDays: SCHEDULER_CONFIG.weekDays, weekStartOn: SCHEDULER_CONFIG.weekStartOn, startHour: SCHEDULER_CONFIG.startHour, endHour: SCHEDULER_CONFIG.endHour }}
                                day={{ startHour: SCHEDULER_CONFIG.startHour, endHour: SCHEDULER_CONFIG.endHour, step: SCHEDULER_CONFIG.step }}
                                viewerExtraComponent={(fields, event) => {
                                    const currentUsername = getUsername();
                                    if (event.approvalStatus !== 'pending' || event.memberId !== currentUsername) {
                                        return null;
                                    }
                                    const approver = event.designatedApprover;
                                    const approverLink = approver
                                        ? <Link to={`/members/${approver.id}/view`}>{approver.name}</Link>
                                        : null;
                                    return (
                                        <div style={{ display: 'flex' }}>
                                            <Icon className="exclamation circle" color="red" />
                                            <div>
                                                {approver?.status !== 'active' && approver ? (
                                                    <>The designated approver {approverLink} is out right now. You may{' '}
                                                        <Link onClick={() => escalateCalendarEvent(event.event_id)}>
                                                            escalate approval to their supervisor.
                                                        </Link>
                                                    </>
                                                ) : (
                                                    <>This absence is waiting on approval from {approverLink}</>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                    </div>
                    </>
                )}
            </LoadingWrapper>
        </div>
    );
};
